import type { KnockGuideStep } from "@knocklabs/client";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import { logError } from "@src/appV2/lib/analytics/log";

import { type GuideButtonAction, type GuideButtonContent } from "../types";

export interface HandleGuideActionParams {
  action?: GuideButtonAction;
  content: GuideButtonContent;
  key: string;
  navigate: (path: string) => void;
  openUrl: (url: string) => Promise<void>;
  step: KnockGuideStep;
}

/**
 * Routes a Knock guide button click to the right navigation primitive based on the action URL.
 *
 * Order matters: `markAsInteracted` and the consumer `onClick` run synchronously before any async
 * navigation so Safari's popup blocker keeps the user-gesture chain to `openUrl`.
 */
export async function handleGuideAction(params: HandleGuideActionParams): Promise<void> {
  const { action, content, key, step, navigate, openUrl } = params;

  if (!content.action) {
    return;
  }

  step.markAsInteracted({
    metadata: {
      action: content.action,
      ...action?.metadata,
    },
  });
  action?.onClick?.(content);

  if (isRelativePath(content.action)) {
    navigate(content.action);
    return;
  }

  if (isAbsoluteUrl(content.action)) {
    try {
      await openUrl(content.action);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.KNOCK_BROWSER_OPEN_FAILED, { error });
    }

    return;
  }

  if (/^(mailto|tel|sms):/.test(content.action)) {
    globalThis.location.href = content.action;
    return;
  }

  if (!action?.onClick) {
    logError(APP_V2_APP_EVENTS.KNOCK_GUIDE_ACTION_NOT_HANDLED, {
      error: "Guide action not handled",
      metadata: {
        action: content.action,
        key,
      },
    });
  }
}

function isAbsoluteUrl(value: string) {
  try {
    const url = new URL(value);
    return Boolean(url.protocol && url.host);
  } catch {
    return false;
  }
}

function isRelativePath(value: string): boolean {
  return value.startsWith("/");
}
